/**
 * Receipts open and locked (Miles, 2026-10-26): a receipt stays open for members to mark which
 * items were theirs until Miles locks it. Receipts dated before RECEIPTS_OPEN_FROM (everything in
 * the ledger when this shipped, the Sheet import included) start locked; any of them can be
 * opened again. The owner and a club treasurer can still tick anyone on a locked receipt.
 *
 * Stored in the group's settings as an explicit map (key -> locked), changed with an ordinary
 * settings.update, so it shows in History and Undo works like any change.
 */
import {
  RECEIPT_ID_PREFIX,
  receiptKey,
  tripKey,
  type GroupState,
  type IsoDate,
  type Settings,
} from "./types.ts";

/** Receipts dated on or after this day start open; earlier ones start locked. */
export const RECEIPTS_OPEN_FROM: IsoDate = "2026-10-27";

export interface ReceiptRef {
  date: IsoDate;
  store: string;
  receipt?: string;
  /** The receipt by id (Miles, 2026-10-28): its lock follows it through any rename. */
  receiptId?: string;
}

const keyOf = (r: ReceiptRef) => receiptKey(r);

/** Whether members can still mark items on this receipt. */
export function receiptIsOpen(
  settings: Pick<Settings, "receiptLocks" | "receiptsOpenByDefault">,
  r: ReceiptRef,
): boolean {
  // By id first; a receipt locked or opened by store, date and label (before ids) still counts
  // until it's renamed.
  const set =
    settings.receiptLocks?.[keyOf(r)] ??
    (r.receiptId ? settings.receiptLocks?.[tripKey(r.date, r.store, r.receipt ?? "")] : undefined);
  if (set !== undefined) return !set;
  // Once the receipts already there are locked by name, every new one starts open, whatever
  // the date printed on it (a trip from last week scanned today is open).
  if (settings.receiptsOpenByDefault) return true;
  return r.date >= RECEIPTS_OPEN_FROM;
}

/**
 * The one-time change that locks, by name, every receipt in the group that isn't open now, and
 * from then on opens new receipts whatever their date. Nothing open or locked today changes.
 * Null when it's already been made.
 */
export function receiptBaselinePatch(
  state: GroupState,
): { receiptLocks: Record<string, boolean>; receiptsOpenByDefault: true } | null {
  if (state.settings.receiptsOpenByDefault) return null;
  const locks = { ...(state.settings.receiptLocks ?? {}) };
  for (const it of Object.values(state.items)) {
    const key = keyOf(it);
    if (locks[key] === undefined && !receiptIsOpen(state.settings, it)) locks[key] = true;
  }
  return { receiptLocks: locks, receiptsOpenByDefault: true };
}

/** The settings patch that locks (true) or opens (false) one receipt. */
export function receiptLockPatch(
  settings: Pick<Settings, "receiptLocks" | "receiptsOpenByDefault">,
  r: ReceiptRef,
  locked: boolean,
): { receiptLocks: Record<string, boolean> } {
  return { receiptLocks: { ...(settings.receiptLocks ?? {}), [keyOf(r)]: locked } };
}

/** Whether a member may change their own tick on this item (false: gone, or receipt locked). */
export function memberCanTick(state: GroupState, itemId: string): boolean {
  const it = state.items[itemId];
  return !!it && receiptIsOpen(state.settings, it);
}

/**
 * A lock key back into a receipt, for History. A receipt id is looked up in `state` (its items
 * say today's store and date); older keys are date|store|label.
 */
export function receiptKeyParts(key: string, state?: GroupState): ReceiptRef {
  if (key.startsWith(RECEIPT_ID_PREFIX)) {
    const receiptId = key.slice(RECEIPT_ID_PREFIX.length);
    const it = state && Object.values(state.items).find((i) => i.receiptId === receiptId);
    return {
      date: it?.date ?? "",
      store: it?.store ?? "",
      receipt: it?.receipt ?? "",
      receiptId,
    };
  }
  const [date = "", store = "", receipt = ""] = key.split("|");
  return { date, store, receipt };
}
